import React from 'react';
import { motion } from 'framer-motion';
import { FaCheckCircle } from 'react-icons/fa';

const apexFeatures = [
  { id: "voice-agent", text: "24/7 AI Voice Agent (250 AI minutes included per attorney/month)" },
  { id: "textback", text: "Missed-call text-back (instant)" },
  { id: "forms", text: "Unlimited HIPAA-compliant intake forms, mobile-optimized" },
  { id: "routing", text: "AI lead qualification and case-type routing" },
  { id: "auto-respond", text: "Auto-responder (SMS + Email), under 30 seconds" },
  { id: "followup", text: "Automated follow-up sequences" },
  { id: "med-records", text: "Medical records request automation" },
  { id: "doc-remind", text: "Document reminder sequences" },
  { id: "esign", text: "E-signature integration" },
  { id: "pipeline", text: "Real-time lead pipeline dashboard" },
  { id: "source-track", text: "Source tracking and reporting" },
  { id: "spanish", text: "Spanish language support" },
  { id: "hipaa", text: "BAA included, full HIPAA compliance" },
  { id: "training", text: "Training session included" },
  { id: "support", text: "Priority support" }
];

const enterpriseFeatures = [
  { id: "includes-apex", text: "Everything in The Apex System, plus:" },
  { id: "custom-voice", text: "Custom AI voice & persona" },
  { id: "multi-loc", text: "Multi-location support" },
  { id: "custom-workflow", text: "Custom workflow automation" },
  { id: "api", text: "API integrations" },
  { id: "account-mgr", text: "Dedicated account manager" },
  { id: "exec-report", text: "Executive reporting" }
];

const PricingSection = () => {
  const handleBookDemo = () => {
    window.open('https://calendly.com/contact-apexlegalsystems/30min', '_blank');
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50" id="pricing">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Simple Pricing. <span className="gradient-text">Serious Results.</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Choose the plan that fits your firm. Scale as you grow.
          </p>
        </motion.div>

        {/* How Our Pricing Works */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 max-w-4xl mx-auto"
        >
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">How Our Pricing Works</h3>
            <p className="text-gray-400 leading-relaxed">
              We keep pricing simple: $1,500 per month and $3,000 setup for each attorney at your firm. Every attorney seat includes a support staff seat at no extra cost — no need to count paralegals or intake staff separately. Firms with 11 or more attorneys, multiple office locations, or custom integration needs fall under our Enterprise plan, quoted individually.
            </p>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-12">
          {/* The Apex System */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="pricing-card rounded-2xl p-8 relative ring-2 ring-emerald-500"
            data-testid="pricing-tier-apex"
          >
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-slate-900 px-4 py-2 rounded-full text-sm font-bold">
                MOST FIRMS
              </span>
            </div>

            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">The Apex System</h3>
            </div>

            <div className="mb-6">
              <div className="flex items-baseline mb-2">
                <span className="text-4xl font-bold gradient-text">$1,500</span>
                <span className="text-gray-400 ml-2">/month per attorney</span>
              </div>
              <p className="text-gray-400 text-sm mb-2">$3,000 one-time setup per attorney</p>
              <p className="text-emerald-400 text-sm font-semibold">Each attorney seat includes 1 support staff seat at no extra cost</p>
            </div>

            <div className="mb-6 text-sm text-gray-400">
              <div className="flex justify-between">
                <span>Go-Live:</span>
                <span className="text-white font-semibold">7-14 business days depending on firm size</span>
              </div>
            </div>

            <button
              onClick={handleBookDemo}
              className="w-full py-3 rounded-lg font-bold transition-all mb-6 bg-emerald-500 hover:bg-emerald-600 text-slate-900"
              data-testid="pricing-cta-apex"
            >
              Book Discovery Call
            </button>

            <div className="space-y-3">
              {apexFeatures.map((feature) => (
                <div key={feature.id} className="flex items-start">
                  <FaCheckCircle className="text-emerald-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Enterprise */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="pricing-card rounded-2xl p-8 relative"
            data-testid="pricing-tier-enterprise"
          >
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-white mb-2">Enterprise</h3>
              <p className="text-gray-400 text-sm">For firms with 11+ attorneys, multiple office locations, or custom integration needs.</p>
            </div>

            <div className="mb-6 py-4">
              <p className="text-gray-300 text-base leading-relaxed">Custom quote — schedule a consultation</p>
            </div>

            <button
              onClick={handleBookDemo}
              className="w-full py-3 rounded-lg font-bold transition-all mb-6 bg-slate-700 hover:bg-slate-600 text-white"
              data-testid="pricing-cta-enterprise"
            >
              Schedule Enterprise Consultation
            </button>

            <div className="space-y-3">
              {enterpriseFeatures.map((feature) => (
                <div key={feature.id} className="flex items-start">
                  <FaCheckCircle className="text-emerald-400 mt-1 mr-3 flex-shrink-0" />
                  <span className="text-gray-300 text-sm">{feature.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom notes */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-4xl mx-auto"
        >
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
            <div className="grid md:grid-cols-2 gap-6 text-left mb-6">
              <div className="flex items-start">
                <FaCheckCircle className="text-emerald-400 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-300">Flexible month-to-month plans. Ask about our discount on yearly commitments!</span>
              </div>
              <div className="flex items-start">
                <FaCheckCircle className="text-emerald-400 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-300">100% setup fee due to begin your build. Monthly billing starts on your go-live date.</span>
              </div>
              <div className="flex items-start">
                <FaCheckCircle className="text-emerald-400 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-300">Full HIPAA compliance + BAA signed before go-live</span>
              </div>
              <div className="flex items-start">
                <FaCheckCircle className="text-emerald-400 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-300">AI receptionist included on all plans</span>
              </div>
              <div className="flex items-start">
                <FaCheckCircle className="text-emerald-400 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-300">Cancel anytime with 30 days notice</span>
              </div>
              <div className="flex items-start">
                <FaCheckCircle className="text-emerald-400 mt-1 mr-3 flex-shrink-0" />
                <span className="text-gray-300">All user seats included - no hidden fees</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PricingSection;
